/*
 * Purpose: LLM-backed outcome planner (OpenAI) with structured JSON output
 * Last modified: 2026-06-25 | Agent
 * Completeness: 85
 */
import { hasResolvedSecret, resolveSecret } from '@/lib/resolveSecret';
import { BLOCK_REGISTRY } from '@/services/blocks/registry';
import { listConnectorCatalogForPrompt } from '@/services/connectors/library/store';
import { API_CATALOG } from '@/services/autoblocks/connectorCatalog';
import { llmPlanSchema, type LlmPlanPayload } from './planSchema';
import type { OutcomePlan } from './types';
import { readEnvOr } from '@/lib/runtimeEnv';

const genId = () => `plan-${Date.now().toString(36)}`;

const MODEL = readEnvOr('VITE_OPENAI_MODEL', 'gpt-4o-mini');

function buildSystemPrompt(): string {
  const blocks = BLOCK_REGISTRY.map(
    (b) => `- ${b.id} (${b.category}/${b.subtype}): ${b.description}`
  ).join('\n');

  const templates = [
    'linkedin_job_hunt_pipeline',
    'job_application_tracker',
    'job_followup_reminder',
    'receipt_email_to_tracker',
    'appointment_reminder',
  ].join(', ');

  const connectors = listConnectorCatalogForPrompt();
  const apis = API_CATALOG.map((a) => `${a.id}: ${a.baseUrl}`).join('\n');

  return `You are AutoBlocks — an outcome compiler for workflow automation. You OWN all connectors — never reference Make, Zapier, or n8n.

Given a user's desired OUTCOME (not implementation steps), produce a JSON plan that:
1. Interprets the goal in plain language
2. Lists ordered steps using ONLY these block types:
${blocks}
3. Uses AutoBlocks connector IDs from our library (recommend in connectors[] with id matching ab.*):
${connectors}
4. Uses APIs from: ${apis}
5. Proposes customBlocks (transform or http tools) when no primitive fits
6. Includes dryRunFixture — realistic sample input for the trigger
7. Sets useGmailInbound true when the flow starts from email/Gmail/job alerts/receipts
8. Optionally set matchedTemplateId if outcome matches: ${templates}

Rules:
- Never ask the user to use external automation products
- Never ask the user to write code; embed transform logic in step config.transform strings
- Use fact-only AI prompts for resume/cover letter steps
- List assumptions and gaps honestly (API limits, OAuth, CORS, LinkedIn ToS)
- confidence 0-1 based on how well the outcome maps to available blocks
- Return ONLY valid JSON matching the schema fields`;
}

export function hasLlmPlannerKey(): boolean {
  return hasResolvedSecret('OPENAI_API_KEY');
}

export async function planFromLlm(rawOutcome: string): Promise<OutcomePlan | null> {
  const apiKey = resolveSecret('OPENAI_API_KEY');
  if (!apiKey) return null;

  const userPrompt = `OUTCOME:\n${rawOutcome}\n\nReturn JSON with keys: interpretedGoal, confidence, matchedTemplateId?, useGmailInbound?, steps[], connectors[], apis[], customBlocks[], assumptions[], gaps[], dryRunFixture{}`;

  let response: Response;
  try {
    response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        model: MODEL,
        temperature: 0.2,
        response_format: { type: 'json_object' },
        messages: [
          { role: 'system', content: buildSystemPrompt() },
          { role: 'user', content: userPrompt },
        ],
      }),
    });
  } catch {
    return null;
  }

  if (!response.ok) {
    console.warn('[llmPlanner] OpenAI error', response.status);
    return null;
  }

  const data = (await response.json()) as {
    choices?: Array<{ message?: { content?: string } }>;
  };
  const content = data.choices?.[0]?.message?.content;
  if (!content) return null;

  let parsed: LlmPlanPayload;
  try {
    parsed = llmPlanSchema.parse(JSON.parse(content));
  } catch (e) {
    console.warn('[llmPlanner] Invalid plan JSON', e);
    return null;
  }

  return {
    id: genId(),
    rawOutcome: rawOutcome.trim(),
    interpretedGoal: parsed.interpretedGoal,
    confidence: parsed.confidence,
    steps: parsed.steps,
    connectors: parsed.connectors.map((c) => ({
      ...c,
      docsUrl: c.docsUrl || 'https://developers.google.com/gmail/api',
    })),
    apis: parsed.apis,
    customBlocks: parsed.customBlocks,
    assumptions: parsed.assumptions,
    gaps: parsed.gaps,
    dryRunFixture: parsed.dryRunFixture,
    matchedTemplateId: parsed.matchedTemplateId,
    plannerSource: 'llm',
    useGmailInbound: parsed.useGmailInbound,
  };
}
